import { DealerLedgerViewModel, LedgerPeriod, type YearSummary } from '@lumora/core';
import { useViewModel } from '@lumora/ui';
import { useEffect, useMemo } from 'react';
import Icons from '../component/Icons';
import { formatCurrency } from '../component/CarCard';
import { client } from '../../client';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AdminOverview = ({ summary }: { summary: YearSummary }) => {
    const ledgerVm = useMemo(() => new DealerLedgerViewModel(client), []);
    const monthlyData = useViewModel(ledgerVm);

    useEffect(() => {
        void ledgerVm.loadLedger(LedgerPeriod.all());
    }, [ledgerVm]);

    // Grouping by month for chart
    const monthlyStats = Array.from({ length: 6 }).map((_, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        
        const rev = monthlyData.sales.filter(s => s.soldDate.startsWith(monthStr)).reduce((sum, s) => sum + s.soldPrice, 0);
        const exp = monthlyData.expenses.filter(e => e.date.startsWith(monthStr)).reduce((sum, e) => sum + e.amount, 0);
        
        return { label: d.toLocaleString('default', { month: 'short' }), rev, exp };
    }).reverse();

    const chartData = {
        labels: monthlyStats.map(s => s.label),
        datasets: [
            {
                label: 'Gross Revenue',
                data: monthlyStats.map(s => s.rev),
                backgroundColor: 'rgba(34, 197, 94, 0.8)',
            },
            {
                label: 'Expenses',
                data: monthlyStats.map(s => s.exp),
                backgroundColor: 'rgba(239, 68, 68, 0.8)',
            }
        ],
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 w-full">
            <h1 className="text-4xl font-black italic tracking-tighter uppercase text-gray-900 mb-2">Command Center</h1>
            <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest mb-10">High-Level Operations Overview</p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-gray-400 mb-2"><Icons.Car /></div>
                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">YTD Vehicles Sold</span>
                    <span className="text-3xl font-black text-gray-900 tracking-tighter">{summary.salesCount || 0}</span>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-gray-400 mb-2"><Icons.Dollar /></div>
                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">YTD Gross Revenue</span>
                    <span className="text-3xl font-black text-green-600 tracking-tighter">{formatCurrency(summary.revenue || 0)}</span>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-gray-400 mb-2"><Icons.Dashboard /></div>
                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">YTD OpEx (Expenses)</span>
                    <span className="text-3xl font-black text-red-600 tracking-tighter">{formatCurrency(summary.expenses || 0)}</span>
                </div>
                <div className="bg-gray-900 p-6 rounded-2xl shadow-lg shadow-gray-200">
                    <div className="text-gray-500 mb-2"><Icons.Shield /></div>
                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">YTD Net Profit</span>
                    <span className={`text-3xl font-black tracking-tighter ${summary.netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(summary.netProfit || 0)}
                    </span>
                </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-10">
                <h3 className="text-xl font-bold uppercase tracking-widest text-gray-800 mb-6">6-Month Financial Trend</h3>
                <div className="h-[300px]">
                    <Bar 
                        data={chartData} 
                        options={{ responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } }} 
                    />
                </div>
            </div>
        </div>
    );
};

export default AdminOverview;